import React, { memo, useEffect } from "react"
import { makeStyles } from "@material-ui/core/styles"
import clsx from "clsx"
import { Redirect } from "react-router"
import LayoutMap from "../LayoutMap"
import Graphs from "../Graphs"
import { privileges } from "constants/auth"
import { useAuthorization } from "utils/auth"
import PropTypes, { arrayOf } from "prop-types"
import { componentOptions } from "../../constants/componentOptions"

const useStyles = makeStyles(() => ({
	rootLayoutView: {
		display: "grid",
		gridTemplateColumns: "repeat(2, 1fr)",
		gridTemplateRows: "repeat(1, 1fr)",
		height: "100%",
		overflow: "hidden",
	},
	gridDual: {
		gridTemplateAreas: `
      'item0 item1'
      'item0 item1'
    `,
	},
	gridSingle: {
		gridTemplateAreas: `
      'item0 item0'
      'item0 item0'
    `,
	},
	item0: {
		gridArea: "item0",
	},
	item1: {
		gridArea: "item1",
	},
}))

const LayoutView = memo(
	({
		params,
		match,
		mLocIds,
		userPrivileges,
		subscribeNodeIdUpdates,
		subscribeMasterIdUpdates,
		subscribeNodeSyncBitmasksUpdate,
		fetchLayoutDetailsBySiteUuid,
		fetchMasterDataByLayoutId,
		layoutDetails,
		fetchMasterDetails,
		fetchNodeDetails,
		masterDetails,
		timestamp,
		handleRetrieveComponentsScanned,
		deviceIdAssignment,
	}) => {
		const accessAllowed = useAuthorization(userPrivileges)
		const classes = useStyles()
		const layoutId = match.params.layoutId
		const deviceType = (masterDetails[((layoutDetails || {}).mLocIds || {})[0]] || {})["deviceType"]

		useEffect(() => {
			if (layoutId) {
				fetchLayoutDetailsBySiteUuid(layoutId)
			}
		}, [layoutId, fetchLayoutDetailsBySiteUuid])

		useEffect(() => {
			if (layoutId && deviceType && accessAllowed(privileges.DATA_SNAPSHOT)) {
				fetchMasterDataByLayoutId(layoutId, userPrivileges, deviceType, timestamp)
			}
		}, [layoutId, fetchMasterDataByLayoutId, accessAllowed, userPrivileges, deviceType, timestamp])

		useEffect(() => {
			fetchMasterDetails(layoutId)
		}, [fetchMasterDetails, layoutId])

		useEffect(() => {
			if (Object.keys(masterDetails).length) {
				fetchNodeDetails(layoutId, masterDetails)
			}
		}, [fetchNodeDetails, layoutId, masterDetails])

		useEffect(() => {
			if (layoutId && deviceIdAssignment) {
				const requests = []
				for (let component of componentOptions) {
					requests.push(handleRetrieveComponentsScanned(layoutId, component.urlName, component.isMaster))
				}
				Promise.all(requests).catch((ex) => {
					console.error(ex)
				})
			}
		}, [handleRetrieveComponentsScanned, layoutId, deviceIdAssignment])

		useEffect(() => {
			if (layoutId) {
				return subscribeNodeIdUpdates(layoutId)
			}
		}, [layoutId, subscribeNodeIdUpdates])

		useEffect(() => {
			if (layoutId) {
				return subscribeNodeSyncBitmasksUpdate(layoutId)
			}
		}, [layoutId, subscribeNodeSyncBitmasksUpdate])

		useEffect(() => {
			if (layoutId) {
				return subscribeMasterIdUpdates(layoutId)
			}
		}, [layoutId, subscribeMasterIdUpdates])

		let redirect = false
		const view = params.get("view")
		if (view === null) {
			params.set("view", "layout")
			redirect = true
		}
		const mLocationIds = params.get("locIds")
		if (!mLocationIds && mLocIds.length > 0) {
			params.set("locIds", mLocIds[0])
			redirect = true
		}
		if (redirect) {
			return <Redirect to={{ pathname: match.url, search: "?" + params.toString() }} />
		}

		let viewList = view.split(",")
		if (!accessAllowed(privileges.DATA_HISTORY)) {
			viewList = viewList.filter((view) => view !== "chart")
		}
		const splitView = viewList.length > 1

		return (
			<div
				className={clsx(classes.rootLayoutView, {
					[classes.gridDual]: splitView,
					[classes.gridSingle]: !splitView,
				})}
			>
				{viewList.includes("layout") ? (
					<div className={classes.item0}>
						<LayoutMap />
					</div>
				) : null}
				{viewList.includes("chart") ? (
					<div className={clsx({ [classes.item0]: !splitView, [classes.item1]: splitView })}>
						<Graphs />
					</div>
				) : null}
			</div>
		)
	},
)

LayoutView.displayName = "LayoutView"

LayoutView.propTypes = {
	masterDetails: PropTypes.object.isRequired,
	params: PropTypes.object.isRequired,
	match: PropTypes.object.isRequired,
	mLocIds: arrayOf(PropTypes.string).isRequired,
	userPrivileges: PropTypes.any.isRequired,
	subscribeNodeIdUpdates: PropTypes.func.isRequired,
	subscribeMasterIdUpdates: PropTypes.func.isRequired,
	subscribeNodeSyncBitmasksUpdate: PropTypes.func.isRequired,
	fetchLayoutDetailsBySiteUuid: PropTypes.func.isRequired,
	fetchMasterDataByLayoutId: PropTypes.func.isRequired,
	layoutDetails: PropTypes.object.isRequired,
	fetchMasterDetails: PropTypes.func.isRequired,
	fetchNodeDetails: PropTypes.func.isRequired,
	timestamp: PropTypes.number.isRequired,
	handleRetrieveComponentsScanned: PropTypes.func.isRequired,
	deviceIdAssignment: PropTypes.bool.isRequired,
}

export default LayoutView
